import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Badge, Button, Col, Row } from "reactstrap"
import { CanView } from "components/Common/CanView"
import ButtonTooltip from "components/Common/ButtonTooltip"
import TableContainer from "components/Common/TableContainer"
import PageWrapper from "components/Common/PageWrapper"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import { useListParams } from "hooks/useListParams"
import {
  useMoneyReceipts,
  useDeleteMoneyReceipt,
  useCancelMoneyReceipt,
} from "hooks/useMoneyReceipt"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { downloadAndOpenFile } from "api/pdfApi"
import { useToastMessage } from "hooks/useToastMessage"
import { ACCESS } from "constants/accessPermissions"
import ConfirmModal from "components/Common/ConfirmModal"
import DeleteModal from "components/Common/DeleteModal"
import PrintDateModal from "./PrintDateModal"

const paymentBadgeColor = status => {
  if (status === "overpaid") return "primary"
  if (status === "fully_paid") return "success"
  if (status === "partially_paid") return "info"
  if (status === "refunded") return "secondary"
  return "warning"
}

const getReceiptCustomerName = item =>
  item?.pdf_content?.customer_info?.name || item?.customer?.name || "-"

const getReceiptCustomerMobile = item =>
  item?.pdf_content?.customer_info?.mobile || item?.customer?.mobile || "-"

const getReceiptVehicleLabel = item =>
  item?.pdf_content?.vehicle_info?.name ||
  item?.vehicle?.stock_id ||
  item?.pdf_content?.vehicle_info?.stock_id ||
  "-"

const getReceiptVehicleIdentity = item =>
  item?.pdf_content?.vehicle_info?.full_chassis ||
  item?.pdf_content?.vehicle_info?.chassis_no ||
  item?.vehicle?.full_chassis ||
  "-"

const getReceiptVehicleMeta = item =>
  [
    item?.pdf_content?.vehicle_info?.year_model,
    item?.pdf_content?.vehicle_info?.color,
    item?.pdf_content?.vehicle_info?.fuel,
  ]
    .filter(Boolean)
    .join(" | ")

const MoneyReceiptListPage = () => {
  document.title = "Money Receipt List | Aurobil Cars"

  const navigate = useNavigate()
  const location = useLocation()
  const { handleResponse, handleError } = useToastMessage()
  const {
    params,
    setPage,
    setSearch,
    updateParams,
    resetParams,
    hasActiveFilters,
  } = useListParams({
    page: 1,
    search: "",
  })

  const focusId = params?.mr_focus ?? null
  const focusRowRef = useRef(null)

  const [printDateUrl, setPrintDateUrl] = useState(null)
  const [printDateDefault, setPrintDateDefault] = useState("")
  const [isPrinting, setIsPrinting] = useState(false)
  const [deleteModal, setDeleteModal] = useState(null)
  const [cancelModal, setCancelModal] = useState(null)

  const openPrintDateModal = (url, defaultDate = "") => {
    setPrintDateDefault(defaultDate || "")
    setPrintDateUrl(url)
  }

  const handlePrintDateConfirm = async printDate => {
    const url = printDateUrl
    setPrintDateUrl(null)
    if (!url) return
    setIsPrinting(true)
    try {
      await downloadAndOpenFile({ url, params: { print_date: printDate || "" } })
    } catch (error) {
      handleError(error)
    } finally {
      setIsPrinting(false)
    }
  }

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: params.per_page ?? 10,
      search: params.search ?? "",
    }),
    [params]
  )

  const { data, isLoading, refetch, isRefetching } = useMoneyReceipts(queryParams)
  const { mutateAsync: deleteMoneyReceipt, isPending: isDeleting } =
    useDeleteMoneyReceipt()
  const { mutateAsync: cancelMoneyReceipt, isPending: isCancelling } =
    useCancelMoneyReceipt()

  const fetchedData = data?.data?.data ?? []
  const paginationInfo = data?.data

  useEffect(() => {
    if (!focusId) return
    const node = focusRowRef.current
    if (!node) return
    node.scrollIntoView({ behavior: "smooth", block: "center" })
    node.classList.add("table-warning")
    const timer = setTimeout(() => {
      node.classList.remove("table-warning")
      updateParams({ mr_focus: undefined })
    }, 2500)
    return () => clearTimeout(timer)
  }, [focusId, fetchedData, updateParams])

  const handleDelete = async item => {
    try {
      const response = await deleteMoneyReceipt({ id: item.id })
      handleResponse(response, "Money receipt deleted successfully.", true)
      setDeleteModal(null)
      refetch()
    } catch (error) {
      handleError(error, "Failed to delete money receipt.")
    }
  }

  const handleCancel = async item => {
    try {
      await cancelMoneyReceipt(item.id)
      setCancelModal(null)
      refetch()
    } catch (error) {
      handleError(error, "Failed to cancel money receipt.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Receipt",
        cell: ({ row }) => {
          const item = row.original
          return (
            <div>
              <div className="fw-bold text-primary">{item?.mr_no ?? "-"}</div>
              <div className="small text-muted">
                Issued: {formatReadableDate(item?.issued_date, "MMM d, y")}
              </div>
              <div className="small text-muted">
                Quotation: {item?.quotation?.qid ?? "-"}
              </div>
              <div className="d-sm-none mt-1">
                <div className="small text-muted">{getReceiptCustomerName(item)}</div>
                <div className="small text-muted">{getReceiptCustomerMobile(item)}</div>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  <Badge color={item?.status === "active" ? "success" : "secondary"}>
                    {item?.status ?? "active"}
                  </Badge>
                  <Badge color={paymentBadgeColor(item?.payment_status)}>
                    {String(item?.payment_status ?? "pending").replaceAll("_", " ")}
                  </Badge>
                </div>
              </div>
            </div>
          )
        },
      },
      {
        header: "Customer",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div>
              <div className="fw-semibold">{getReceiptCustomerName(item)}</div>
              <div className="small text-muted">{getReceiptCustomerMobile(item)}</div>
            </div>
          )
        },
      },
      {
        header: "Vehicle",
        meta: { className: "d-none d-md-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div>
              <div>{getReceiptVehicleLabel(item)}</div>
              <div className="small text-muted">{getReceiptVehicleIdentity(item)}</div>
              {getReceiptVehicleMeta(item) ? (
                <div className="small text-muted">{getReceiptVehicleMeta(item)}</div>
              ) : null}
            </div>
          )
        },
      },
      {
        header: "Financial",
        meta: { className: "d-none d-lg-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          const carPrice = Number(item?.car_price ?? 0)
          const regCost = Number(item?.registration_cost ?? 0)
          const addlCost = Number(item?.additional_cost_total ?? 0)
          const discount = Number(
            item?.pdf_content?.vehicle_info?.discount ?? 0
          )
          return (
            <div>
              <div className="small text-muted">
                Price: {formatBDT(carPrice)}
              </div>
              {regCost > 0 && (
                <div className="small text-muted">
                  Reg: {formatBDT(regCost)}
                </div>
              )}
              {addlCost > 0 && (
                <div className="small text-muted">
                  Add'l: {formatBDT(addlCost)}
                </div>
              )}
              {discount > 0 && (
                <div className="small text-danger">
                  Discount: -{formatBDT(discount)}
                </div>
              )}
              <div className="fw-bold border-top pt-1 mt-1">
                Total: {formatBDT(item?.total_amount)}
              </div>
              <div className="small text-success">
                Paid: {formatBDT(item?.amount_paid)}
              </div>
              <div className="small text-muted">
                Due: {formatBDT(item?.due_amount)}
              </div>
              {Number(item?.overpaid_amount ?? 0) > 0 && (
                <div className="small text-warning">
                  Overpaid: {formatBDT(item?.overpaid_amount)}
                </div>
              )}
            </div>
          )
        },
      },
      {
        header: "Status",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          return (
            <div className="d-flex flex-column gap-1">
              <Badge color={item?.status === "active" ? "success" : "secondary"}>
                {item?.status ?? "active"}
              </Badge>
              <Badge color={paymentBadgeColor(item?.payment_status)}>
                {String(item?.payment_status ?? "pending").replaceAll("_", " ")}
              </Badge>
            </div>
          )
        },
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const item = row.original
          const canEdit = item?.status === "active"
          const canCancel = item?.status === "active"
          const canDelete = Number(item?.amount_paid ?? 0) === 0 && !isDeleting

          return (
            <ul className="list-unstyled hstack gap-1 mb-0 flex-wrap">
              <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                <li>
                  <ButtonTooltip
                    type="view"
                    btnId={`mr-view-${item.id}`}
                    onClick={() => navigate(`/money-receipts/${item.id}`)}
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.MONEY_RECEIPT.EDIT]}>
                <li>
                  <ButtonTooltip
                    type="edit"
                    color="warning"
                    btnId={`mr-edit-${item.id}`}
                    disabled={!canEdit}
                    onClick={() =>
                      navigate(`/money-receipts/${item.id}/edit`, {
                        state: { from: location },
                      })
                    }
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.MONEY_RECEIPT.VIEW]}>
                <li>
                  <ButtonTooltip
                    type="pdf"
                    color="info"
                    btnId={`mr-pdf-${item.id}`}
                    onClick={() =>
                      openPrintDateModal(
                        `/money-receipts/${item.id}/pdf`,
                        item.issued_date
                      )
                    }
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.MONEY_RECEIPT.CANCEL]}>
                <li>
                  <ButtonTooltip
                    type="cancel"
                    btnId={`mr-cancel-${item.id}`}
                    disabled={!canCancel || isCancelling}
                    onClick={() => setCancelModal(item)}
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.MONEY_RECEIPT.DELETE]}>
                <li>
                  <ButtonTooltip
                    type="delete"
                    btnId={`mr-delete-${item.id}`}
                    disabled={!canDelete}
                    onClick={() => setDeleteModal(item)}
                  />
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [navigate, location, isDeleting, isCancelling]
  )

  return (
    <PageWrapper title="Money Receipt" breadcrumbItem="Money Receipt List">
      <Row className="mt-3 gy-2 align-items-center">
        <Col xs={6}>
          <h5 className="mb-0 card-title">
            Total{" "}
            <span className="badge badge-soft-info">{paginationInfo?.total ?? 0}</span>
          </h5>
        </Col>
        <Col xs={6} className="d-flex justify-content-end align-items-center gap-1">
          <ButtonTooltip
            type="refresh"
            btnId="refreshMoneyReceiptList"
            size={null}
            loading={isRefetching}
            onClick={() => refetch()}
          />
          <CanView access={[ACCESS.MONEY_RECEIPT.CREATE]}>
            <Button color="primary" onClick={() => navigate("/money-receipts/create")}>
              <i className="bx bx-plus" />
              <span className="d-none d-sm-inline ms-1">Add Money Receipt</span>
            </Button>
          </CanView>
          {hasActiveFilters() && (
            <Button color="danger" onClick={() => resetParams([], { page: 1 })}>
              <span className="d-none d-sm-inline">Reset Filters</span>
              <i className="d-sm-none mdi mdi-filter-remove-outline" />
            </Button>
          )}
        </Col>
      </Row>

      <Row className="my-3">
        <Col xs={12} sm={6} md={4}>
          <GlobalInputSearch
            value={params.search ?? ""}
            onChange={setSearch}
            placeholder="Search MR no, customer, vehicle..."
          />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={fetchedData}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading}
        tableClass="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
        customPageSize={queryParams.per_page}
        getRowKey={row => row.id}
        rowProps={row => {
          const isFocus =
            focusId &&
            (String(row.id) === String(focusId) ||
              String(row.mr_no) === String(focusId))
          if (!isFocus) return {}
          return { ref: focusRowRef }
        }}
      />

      <PrintDateModal
        isOpen={Boolean(printDateUrl)}
        toggle={() => setPrintDateUrl(null)}
        onSubmit={handlePrintDateConfirm}
        isSubmitting={isPrinting}
        title="Money Receipt — Print Date"
        defaultDate={printDateDefault}
      />

      <DeleteModal
        show={!!deleteModal}
        onDeleteClick={() => handleDelete(deleteModal)}
        onCloseClick={() => setDeleteModal(null)}
        loading={isDeleting}
      />

      <ConfirmModal
        isOpen={!!cancelModal}
        toggle={() => setCancelModal(null)}
        title="Cancel Money Receipt?"
        message="This will cancel the money receipt. Do you want to proceed?"
        confirmLabel="Cancel Receipt"
        icon="mdi-cancel"
        btnColor="warning"
        onConfirm={() => handleCancel(cancelModal)}
        isConfirming={isCancelling}
      />
    </PageWrapper>
  )
}

export default MoneyReceiptListPage
